import { useState } from 'react';
import content from './content';
import './App.css';

function ModularPlaceholder() {
  return (
    <div className="modular-placeholder" aria-hidden="true">
      <div className="modular-placeholder__ground" />
      <div className="modular-placeholder__cedar" />
      <div className="modular-placeholder__unit" />
      <div className="modular-placeholder__roof" />
    </div>
  );
}

function SignupForm({ signup }) {
  const [email, setEmail] = useState('');
  const [interest, setInterest] = useState('grow');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  async function handleSubmit(event) {
    event.preventDefault();
    setStatus('submitting');
    setMessage('');

    const payload = { email: email.trim(), interest };

    // Integration point: set REACT_APP_SIGNUP_ENDPOINT (Formspree, Buttondown, custom API).
    if (!signup.endpoint) {
      console.info('[Building Blox signup placeholder]', payload);
      setStatus('success');
      setMessage(signup.success);
      setEmail('');
      return;
    }

    try {
      const response = await fetch(signup.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Signup failed with status ${response.status}`);
      }

      setStatus('success');
      setMessage(signup.success);
      setEmail('');
    } catch (error) {
      console.error(error);
      setStatus('error');
      setMessage(signup.error);
    }
  }

  return (
    <form className="signup-form" onSubmit={handleSubmit} noValidate>
      <div className="field">
        <label htmlFor="email">{signup.emailLabel}</label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          placeholder={signup.emailPlaceholder}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="interest">{signup.interestLabel}</label>
        <select
          id="interest"
          name="interest"
          value={interest}
          onChange={(e) => setInterest(e.target.value)}
        >
          {signup.interests.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <button
        className="btn btn-primary"
        type="submit"
        disabled={status === 'submitting'}
      >
        {status === 'submitting' ? 'Sending…' : signup.button}
      </button>

      {message ? (
        <p
          className={`form-status ${
            status === 'success' ? 'is-success' : 'is-error'
          }`}
          role="status"
        >
          {message}
        </p>
      ) : null}
    </form>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    brand,
    domain,
    tagline,
    studioUrl,
    studioLabel,
    year,
    nav,
    hero,
    concept,
    blox,
    direction,
    signup,
  } = content;

  function closeMenu() {
    setMenuOpen(false);
  }

  return (
    <div className="site">
      <header className="site-header">
        <div className="container header-inner">
          <a className="wordmark" href="#top" onClick={closeMenu}>
            <span className="wordmark-mark" aria-hidden="true" />
            {brand}
          </a>

          <nav className="nav" aria-label="Primary">
            {nav.map((item) => (
              <a key={item.href} href={item.href}>
                {item.label}
              </a>
            ))}
          </nav>

          <div className="header-actions">
            <a className="btn btn-ghost btn-sm" href={`#${signup.id}`}>
              Get updates
            </a>
            <button
              className="menu-toggle"
              type="button"
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span />
            </button>
          </div>
        </div>

        <div
          id="mobile-nav"
          className={`mobile-nav${menuOpen ? ' is-open' : ''}`}
        >
          <div className="container">
            {nav.map((item) => (
              <a key={item.href} href={item.href} onClick={closeMenu}>
                {item.label}
              </a>
            ))}
            <a href={`#${signup.id}`} onClick={closeMenu}>
              Get updates
            </a>
          </div>
        </div>
      </header>

      <main id="top">
        <section className="hero">
          <div className="container hero-grid">
            <div className="hero-copy">
              <p className="eyebrow">{brand}</p>
              <h1>{hero.headline}</h1>
              <p className="hero-support">{hero.support}</p>
              <div className="hero-actions">
                <a className="btn btn-primary" href={hero.primaryCta.href}>
                  {hero.primaryCta.label}
                </a>
                <a className="text-link" href={hero.secondaryCta.href}>
                  {hero.secondaryCta.label}
                </a>
              </div>
            </div>

            <div className="hero-visual">
              <div className="visual-frame">
                {hero.image ? (
                  <img
                    className="hero-image"
                    src={hero.image}
                    alt={hero.imageAlt || ''}
                  />
                ) : (
                  <ModularPlaceholder />
                )}
                <p className="visual-caption">Concept render</p>
              </div>
            </div>
          </div>
        </section>

        <section className="section section-alt" id={concept.id}>
          <div className="container">
            <div className="section-head">
              <h2>{concept.headline}</h2>
              <p>{concept.body}</p>
            </div>
            <ul className="concept-points">
              {concept.points.map((point) => (
                <li key={point.title}>
                  <h3>{point.title}</h3>
                  <p>{point.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="section" id={blox.id}>
          <div className="container">
            <div className="section-head">
              <h2>{blox.headline}</h2>
              <p>{blox.intro}</p>
            </div>

            <div className="blox-grid">
              {blox.items.map((item) => (
                <article
                  key={item.id}
                  className={`blox-card${item.featured ? ' is-featured' : ''}`}
                >
                  <div className="blox-media">
                    <img src={item.image} alt="" />
                  </div>
                  <div className="blox-body">
                    <div className="blox-meta">
                      <h3>{item.name}</h3>
                      <span className="badge">{item.status}</span>
                    </div>
                    <p>{item.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-alt" id={direction.id}>
          <div className="container">
            <div className="direction">
              <div>
                <span className="badge badge-cedar">{direction.badge}</span>
                <h2>{direction.headline}</h2>
                <p>{direction.body}</p>
              </div>
              <div className="direction-visual" aria-hidden="true">
                <img src="/img/grow-blox.png" alt="" />
              </div>
            </div>
          </div>
        </section>

        <section className="section" id={signup.id}>
          <div className="container">
            <div className="signup">
              <h2>{signup.headline}</h2>
              <p>{signup.body}</p>
              <SignupForm signup={signup} />
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container footer-inner">
          <div className="footer-brand">
            <strong>{brand}</strong>
            <span>{domain}</span>
            <span>{tagline}</span>
          </div>
          <p className="footer-meta">
            <a href={studioUrl} rel="noopener noreferrer" target="_blank">
              {studioLabel}
            </a>
            {' · '}
            <span>© {year}</span>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
